import React, {useState} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import colors from '../../../util/colors';
import {mvs} from '../../../util/metrices';
import Bold from '../../../typography/BoldText';
import Medium from '../../../typography/MediumText';
import Regular from '../../../typography/RegularText';
import Icon from 'react-native-vector-icons/Ionicons';
import logo from '../../../assets/img/logo.png';
import { useAuth } from '../../../context/AuthContext';

const LoginScreen = ({navigation}) => {
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isTemporarilyBlocked, setIsTemporarilyBlocked] = useState(false);
  
  const { login } = useAuth();

  const handleLogin = async () => {
    if (!loginValue.trim() || !password.trim()) {
      setLoginError('Please enter both username/email and password.');
      return;
    }

    if (isTemporarilyBlocked) {
      setLoginError('Too many failed attempts. Please wait before trying again.');
      return;
    }

    // Clear previous error
    setLoginError('');

    try {
      setIsSubmitting(true);
      await login({
        login: loginValue.trim(),
        password: password
      });
      
      console.log('Login successful');
      
      // Reset failed attempts on successful login
      setFailedAttempts(0);
      setLoginError('');
      setIsTemporarilyBlocked(false);
      
      // Clear form
      setLoginValue('');
      setPassword('');
      
    } catch (error) {
      console.error('Login error:', error);
      
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Block login temporarily after 5 failed attempts
      if (newFailedAttempts >= 5) {
        setIsTemporarilyBlocked(true);
        setTimeout(() => {
          setIsTemporarilyBlocked(false);
          setFailedAttempts(0);
        }, 30000); // 30 seconds
      }
      
      let errorTitle = 'Login Failed';
      let errorMessage = 'An unexpected error occurred. Please try again.';
      let inlineError = '';
      
      // Handle specific error cases
      if (error.message) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('invalid credentials') || 
            errorMsg.includes('invalid') || 
            errorMsg.includes('wrong') ||
            errorMsg.includes('incorrect')) {
          errorTitle = 'Invalid Credentials';
          errorMessage = 'The username/email or password you entered is incorrect. Please check your credentials and try again.';
          inlineError = `Invalid username/email or password ${newFailedAttempts >= 3 ? `(${newFailedAttempts} failed attempts)` : ''}`;
        } else if (errorMsg.includes('network') || 
                   errorMsg.includes('connection') ||
                   errorMsg.includes('timeout')) {
          errorTitle = 'Connection Error';
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
          inlineError = 'Connection error - please check your internet';
        } else if (errorMsg.includes('user') && errorMsg.includes('not found')) {
          errorTitle = 'Account Not Found';
          errorMessage = 'No account found with this username or email address. Please check your credentials or contact support.';
          inlineError = 'Account not found';
        } else if (errorMsg.includes('account') && errorMsg.includes('inactive')) {
          errorTitle = 'Account Inactive';
          errorMessage = 'Your account has been deactivated. Please contact support for assistance.';
          inlineError = 'Account is inactive';
        } else {
          // Use the original error message if it's user-friendly
          errorMessage = error.message;
          inlineError = error.message;
        }
      }
      
      // Set inline error for immediate feedback
      setLoginError(inlineError);
      
      // Show detailed alert for more serious errors or after multiple attempts
      if (newFailedAttempts >= 3 || errorMessage.includes('Connection') || errorMessage.includes('inactive')) {
        Alert.alert(errorTitle, errorMessage, [
          { 
            text: 'Try Again',
            style: 'default'
          }
        ]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    Alert.alert(
      'Forgot Password',
      'Password reset functionality will be implemented in a future update.',
      [{ text: 'OK' }]
    );
  };

  const navigateToRegister = () => {
    setShowSignUpModal(true);
  };

  // Clear error when user starts typing
  const handleLoginValueChange = (value) => {
    setLoginValue(value);
    if (loginError) {
      setLoginError('');
    }
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (loginError) {
      setLoginError('');
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={styles.header}>
          <Image
            source={logo}
            style={styles.logo}
            resizeMode="contain"
          />
          <Bold style={styles.title}>Author Dashboard</Bold>
          <Regular style={styles.subtitle}>
            Sign in to manage your content.
          </Regular>
        </View>
        <View style={styles.formContainer}>
          <View style={[styles.inputContainer, loginError && styles.inputContainerError]}>
            <Icon name="person-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="Username or Email"
              placeholderTextColor={colors.textSecondary}
              value={loginValue}
              onChangeText={handleLoginValueChange}
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="username"
              editable={!isSubmitting}
            />
          </View>

          <View style={[styles.inputContainer, loginError && styles.inputContainerError]}>
            <Icon name="lock-closed-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="Password"
              placeholderTextColor={colors.textSecondary}
              value={password}
              onChangeText={handlePasswordChange}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoComplete="password"
              editable={!isSubmitting}
            />
            <TouchableOpacity
              style={styles.eyeIcon}
              onPress={() => setShowPassword(!showPassword)}
              disabled={isSubmitting}>
              {showPassword ? (
                <Icon name="eye-outline" size={20} color={colors.textSecondary} />
              ) : (
                <Icon name="eye-off-outline" size={20} color={colors.textSecondary} />
              )}
            </TouchableOpacity>
          </View>

          {/* Error Message */}
          {loginError ? (
            <View style={styles.errorContainer}>
              <Icon name="alert-circle-outline" size={16} color={colors.error} style={styles.errorIcon} />
              <Regular style={styles.errorText}>{loginError}</Regular>
            </View>
          ) : null}

          {/* <TouchableOpacity 
            style={styles.forgotPassword} 
            onPress={handleForgotPassword}
            disabled={isSubmitting}>
            <Medium style={styles.forgotPasswordText}>Forgot Password?</Medium>
          </TouchableOpacity> */}

          <TouchableOpacity 
            style={[
              styles.loginButton, 
              (isSubmitting || isTemporarilyBlocked) && styles.loginButtonDisabled
            ]} 
            onPress={handleLogin}
            disabled={isSubmitting || isTemporarilyBlocked}>
            {isSubmitting ? (
              <ActivityIndicator color={colors.textLight} size="small" />
            ) : (
              <Bold style={styles.loginButtonText}>
                {isTemporarilyBlocked ? 'Please Wait...' : 'Sign In'}
              </Bold>
            )}
          </TouchableOpacity>

          <View style={styles.footer}>
            <Regular style={styles.footerText}>
              Don't have an account?{' '}
            </Regular>
            <TouchableOpacity 
              onPress={navigateToRegister}
              disabled={isSubmitting}>
              <Medium style={styles.signUpText}>Sign Up</Medium>
            </TouchableOpacity>
          </View>
        </View>

        {/* Sign Up Unavailable Modal */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={showSignUpModal}
          onRequestClose={() => setShowSignUpModal(false)}>
          <View style={styles.modalOverlay}>
            <Pressable 
              style={styles.modalBackdrop}
              onPress={() => setShowSignUpModal(false)}
            />
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Sign Up Currently Unavailable</Text>
              <Text style={styles.modalMessage}>
                We're currently in a closed beta phase. New user registrations will be available soon. 
                Thank you for your interest in Praise Devotion!
              </Text>
              <TouchableOpacity 
                style={styles.modalButton}
                onPress={() => setShowSignUpModal(false)}>
                <Text style={styles.modalButtonText}>Understood</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: mvs(20),
  },
  header: {
    alignItems: 'center',
    marginTop: mvs(60),
    marginBottom: mvs(40),
  },
  logo: {
    width: mvs(250),
    height: mvs(250),
    marginBottom: mvs(20),
  },
  title: {
    fontSize: mvs(28),
    color: colors.black,
    marginBottom: mvs(8),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: mvs(16),
    color: colors.textSecondary,
    textAlign: 'center',
  },
  formContainer: {
    flex: 1,
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: mvs(12),
    marginBottom: mvs(16),
    paddingHorizontal: mvs(16),
    height: mvs(56),
    borderWidth: 1,
    borderColor: colors.accent,
  },
  inputContainerError: {
    borderColor: colors.error,
    backgroundColor: '#fef2f2',
  },
  inputIcon: {
    marginRight: mvs(12),
  },
  input: {
    flex: 1,
    fontSize: mvs(16),
    color: colors.black,
    fontFamily: 'DMSans-Regular',
    paddingVertical: 0,
    backgroundColor: 'transparent',
  },
  eyeIcon: {
    padding: mvs(4),
  },
  forgotPassword: {
    alignSelf: 'flex-end',
    marginBottom: mvs(24),
  },
  forgotPasswordText: {
    fontSize: mvs(14),
    color: colors.primary,
  },
  loginButton: {
    backgroundColor: colors.primary,
    borderRadius: mvs(12),
    height: mvs(56),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: mvs(24),
    shadowColor: colors.primary,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    width: '100%',
  },
  loginButtonDisabled: {
    backgroundColor: colors.accent,
  },
  loginButtonText: {
    fontSize: mvs(18),
    color: colors.textLight,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: mvs(30),
    width: '100%',
  },
  footerText: {
    fontSize: mvs(14),
    color: colors.grey,
  },
  signUpText: {
    fontSize: mvs(14),
    color: colors.primary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    borderRadius: mvs(8),
    paddingHorizontal: mvs(12),
    paddingVertical: mvs(10),
    marginBottom: mvs(16),
    borderWidth: 1,
    borderColor: colors.error,
  },
  errorIcon: {
    marginRight: mvs(8),
  },
  errorText: {
    fontSize: mvs(14),
    color: colors.error,
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000000',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  modalContent: {
    backgroundColor: colors.white,
    marginHorizontal: mvs(20),
    padding: mvs(24),
    borderRadius: mvs(16),
    shadowColor: colors.black,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    maxWidth: mvs(320),
    width: '90%',
  },
  modalTitle: {
    fontSize: mvs(18),
    fontFamily: 'DMSans-Bold',
    color: colors.black,
    textAlign: 'center',
    marginBottom: mvs(16),
  },
  modalMessage: {
    fontSize: mvs(14),
    fontFamily: 'DMSans-Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: mvs(20),
    marginBottom: mvs(24),
  },
  modalButton: {
    backgroundColor: colors.primary,
    borderRadius: mvs(8),
    paddingVertical: mvs(12),
    paddingHorizontal: mvs(24),
    alignSelf: 'center',
  },
  modalButtonText: {
    fontSize: mvs(14),
    fontFamily: 'DMSans-Medium',
    color: colors.white,
  },
});

export default LoginScreen;
